import * as React from 'react'

import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Collapse,
  ListItem,
  MenuItem,
  Link as MuiLink,
  Select,
  Switch,
  Table,
  TableCell,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import {
  Confirm,
  FlexHorizontal,
  FlexVertical,
  MessageCard,
  Spacer,
  createBoard,
  useToasts
} from '@vestaboard/installables'
import {
  useGetMarketplaceListing,
  useGetMarketplaceListingCadencePolicy,
  useGetMarketplaceListingProps,
  useUpdateMarketplaceListing,
  useUpdateMarketplaceListingCadencePolicy,
  useUpdateMarketplaceListingProps
} from '../../../api'

import { Alert } from '@material-ui/lab'
import { CreateMessageModal } from '../../message-v2'
import { CsvTagsImport } from './CsvTagsImport'
import { Link } from 'react-router-dom'
import { MediaUploadButton } from '../../../ui/MediaUploadButton'
import { SelectDeveloper } from '../../developers'
import { SelectMessageSet } from './SelectMessageSet'
import { SelectQa } from '../../questions-and-answers'
import { Tags } from './Tags'
import moment from 'moment'
import { uploadV2Media } from '../../../utils'
import { useCloneMarketplaceListing } from '../hooks/useCloneMarketplaceListing'
import { useHistory } from 'react-router'
import { useUpdateMarketplaceListingV2 } from '../hooks/useUpdateMarketplaceListingV2'
import { useUploadMarketplaceListingIconV2 } from '../hooks/useUploadMarketplaceListingIconV2'
import { useGetInstallableDefinition } from '../../installables/hooks/useGetInstallable'
import { useV2GraphqlMutationErrorHandler, useV2GraphqlQueryErrorHandler } from '../../../utils/errors'
import { useCreateMarketplaceListingProps } from '../hooks/useCreateMarketplaceListing'
import { useCreateMarketplaceListingCadencePolicy } from '../hooks/useCreateMarketplaceListing'
import { CadencePolicyEditor } from './CadencePolicyEditor'
import { V2InstallablePropsEditor } from './V2InstallablePropsEditor'
import { useGetMarketplaceListingFeatureTypeIdsV2 } from '../../../api/useGetMarketplaceListingFeatureTypeIdsV2'
import {
  LEGACY_FEATURE_TYPES,
  PRIMARY_FEATURE_TYPES,
  toEditorialFeatureTypeIds
} from '../../../constants/featureTypes'

type MarketplaceListingDetailProps = IMarketplaceListingDetailProps

interface IMarketplaceListingDetailProps {
  id: string
}

interface IConfigurationSchemaFormProps {
  values: any
  onChange?: (values: any) => void
  schema?: {
    fields: {
      type: 'InstallableConfigurationPickListField'
      title: string
      description: string
      options: string[]
      allowMultiple: boolean
      defaultValue?: number
    }[]
  }
}
type ConfigurationSchemaFormProps = IConfigurationSchemaFormProps

const ConfigurationSchemaForm: React.FC<ConfigurationSchemaFormProps> = props => {
  const [fieldValues, setFieldValues] = React.useState(props.values)
  const { onChange } = props

  React.useEffect(() => {
    onChange?.(fieldValues)
  }, [fieldValues, onChange])

  if (!props.schema) return null

  return (
    <>
      {(props.schema.fields || []).map(field => {
        if (field.type === 'InstallableConfigurationPickListField') {
          return (
            <TableRow key={field.title}>
              <TableCell>{field.title}</TableCell>
              <TableCell>
                <Select
                  variant='outlined'
                  multiple
                  value={fieldValues[field.title] ?? []}
                  onChange={e => setFieldValues({ ...fieldValues, [field.title]: e.target.value as string[] })}>
                  {field.options.map(option => (
                    <ListItem key={option} value={option}>
                      {option}
                    </ListItem>
                  ))}
                </Select>
              </TableCell>
            </TableRow>
          )
        }

        if (field.type === 'InstallableConfigurationNumberBooleanField') {
          const selected =
            fieldValues[field.title] === undefined ? !!field.defaultValue || false : !!fieldValues[field.title]

          return (
            <TableRow key={field.title}>
              <TableCell>{field.title}</TableCell>
              <TableCell>
                <Switch
                  checked={selected}
                  onChange={e => {
                    setFieldValues({ ...fieldValues, [field.title]: selected ? 0 : 1 })
                  }}
                />
              </TableCell>
            </TableRow>
          )
        }

        if (field.type === 'InstallableConfigurationStringField') {
          return (
            <TableRow key={field.title}>
              <TableCell>{field.title}</TableCell>
              <TableCell>
                <TextField
                  multiline
                  value={fieldValues[field.title] ?? ''}
                  onChange={e => setFieldValues({ ...fieldValues, [field.title]: e.target.value as string })}
                  placeholder={field.description}
                  variant='outlined'
                  fullWidth
                />
              </TableCell>
            </TableRow>
          )
        }

        return null
      })}
    </>
  )
}

const CURATED_FEATURE_TYPE_IDS = new Set<string>(
  [...PRIMARY_FEATURE_TYPES, ...LEGACY_FEATURE_TYPES].map(featureType => featureType.id)
)

const FeatureTypeCheckboxRow: React.FC<{
  featureType: { id: string; title: string }
  selectedIds: string[]
  disabled?: boolean
  onToggle: (id: string) => void
}> = ({ featureType, selectedIds, disabled, onToggle }) => (
  <Box>
    <Checkbox
      color='primary'
      checked={selectedIds.includes(featureType.id)}
      disabled={disabled}
      onChange={() => onToggle(featureType.id)}
    />{' '}
    <MuiLink component={Link} to={`/marketplace-listing-feature-type/${featureType.id}`} color='textPrimary'>
      {featureType.title}
    </MuiLink>
  </Box>
)

export const MarketplaceListingDetail: React.FC<MarketplaceListingDetailProps> = props => {
  const history = useHistory()
  const { id } = props
  const [mutateV2] = useUpdateMarketplaceListingV2()
  const [mutate] = useUpdateMarketplaceListing()
  const [createProps] = useCreateMarketplaceListingProps()
  const [createCadencePolicy] = useCreateMarketplaceListingCadencePolicy()
  const [updateProps] = useUpdateMarketplaceListingProps()
  const [updateCadencePolicy] = useUpdateMarketplaceListingCadencePolicy()
  const [getIconUploadUrl] = useUploadMarketplaceListingIconV2()
  const { data, loading, refetch } = useGetMarketplaceListing({ id })
  const listingFeatureTypesResponse = useGetMarketplaceListingFeatureTypeIdsV2(id)
  const listingFeatureTypesWrapped = useV2GraphqlQueryErrorHandler(
    'getMarketplaceListingFeatureTypeIds',
    listingFeatureTypesResponse.loading,
    listingFeatureTypesResponse.data?.marketplaceListingV2,
    listingFeatureTypesResponse.error
  )
  const marketplaceListingPropsResponse = useGetMarketplaceListingProps({
    marketplaceListingId: id
  })
  const marketplaceListingCadencePolicyResponse = useGetMarketplaceListingCadencePolicy({
    marketplaceListingId: id
  })
  const marketplaceListingPropsWrapped = useV2GraphqlQueryErrorHandler(
    'getMarketplaceListingProps',
    marketplaceListingPropsResponse.loading,
    marketplaceListingPropsResponse.data?.marketplaceListingPropsV2,
    marketplaceListingPropsResponse.error,
    { notifyOnNotFound: false }
  )
  const marketplaceListingCadencePolicyWrapped = useV2GraphqlQueryErrorHandler(
    'getMarketplaceListingCadencePolicy',
    marketplaceListingCadencePolicyResponse.loading,
    marketplaceListingCadencePolicyResponse.data?.marketplaceListingCadencePolicyV2,
    marketplaceListingCadencePolicyResponse.error,
    { notifyOnNotFound: false }
  )
  const { data: intallableDefinitionData } = useGetInstallableDefinition({
    installableId: data?.marketplaceListing.installable.id ?? 'not-loaded'
  })
  const installableDefinition =
    intallableDefinitionData?.installableDefinitionV2?.__typename === 'InstallableDefinitionV2'
      ? intallableDefinitionData.installableDefinitionV2.definitionJson
      : undefined
  const { addToast } = useToasts()
  const [clone, { loading: cloning }] = useCloneMarketplaceListing()

  const [previewMessage, setPreviewMessage] = React.useState(createBoard())
  const [isEditingPreviewMessage, setIsEditingPreviewMessage] = React.useState(false)
  const [notePreviewMessage, setNotePreviewMessage] = React.useState(Array(3).fill(Array(15).fill(0)))
  const [isEditingNotePreviewMessage, setIsEditingNotePreviewMessage] = React.useState(false)
  const [installable, setInstallable] = React.useState('')
  const [title, setTitle] = React.useState('')
  const [isSaving, setIsSaving] = React.useState(false)
  const [description, setDescription] = React.useState('')
  const [supportsFlagship, setSupportsFlagship] = React.useState(false)
  const [supportsNote, setSupportsNote] = React.useState(false)
  const [iconUrl, setIconUrl] = React.useState(null as string | null)
  const [iconRef, setIconRef] = React.useState(null as string | null)
  const [year, setYear] = React.useState(0)
  const [month, setMonth] = React.useState(0)
  const [day, setDay] = React.useState(0)
  const [developer, setDeveloper] = React.useState<string | null>(null)
  const [messageSet, setMessageSet] = React.useState<string | null>(null)
  const [noteMessageSet, setNoteMessageSet] = React.useState<string | null>(null)
  const [configuration, setConfiguration] = React.useState({} as any)
  const [categories, setCategories] = React.useState([] as string[])
  const [tags, setTags] = React.useState('')
  const [language, setLanguage] = React.useState(null as string | null | undefined)
  const [version, setVersion] = React.useState(null as string | null | undefined)
  const [carouselMedia1, setCarouselMedia1] = React.useState(null as string | null)
  const [carouselMedia2, setCarouselMedia2] = React.useState(null as string | null)
  const [carouselMedia3, setCarouselMedia3] = React.useState(null as string | null)
  const [carouselMedia1Ref, setCarouselMedia1Ref] = React.useState(null as string | null)
  const [carouselMedia2Ref, setCarouselMedia2Ref] = React.useState(null as string | null)
  const [carouselMedia3Ref, setCarouselMedia3Ref] = React.useState(null as string | null)
  const [noteCarouselMedia1, setNoteCarouselMedia1] = React.useState(null as string | null)
  const [noteCarouselMedia2, setNoteCarouselMedia2] = React.useState(null as string | null)
  const [noteCarouselMedia3, setNoteCarouselMedia3] = React.useState(null as string | null)
  const [noteCarouselMedia1Ref, setNoteCarouselMedia1Ref] = React.useState(null as string | null)
  const [noteCarouselMedia2Ref, setNoteCarouselMedia2Ref] = React.useState(null as string | null)
  const [noteCarouselMedia3Ref, setNoteCarouselMedia3Ref] = React.useState(null as string | null)
  const [shortDescription, setShortDescription] = React.useState(null as string | null)
  const [isFeatured, setIsFeatured] = React.useState(null as boolean | null)
  const [stagingOnly, setStagingOnly] = React.useState(false)
  const [free, setFree] = React.useState(false)
  const [showErrors, setShowErrors] = React.useState(false)
  const [featureTypes, setFeatureTypes] = React.useState<string[]>([])
  const [legacyFeatureTypesOpen, setLegacyFeatureTypesOpen] = React.useState(false)
  const [questionAndAnswerId, setQuestionAndAnswerId] = React.useState(null as string | null)
  const [noteQuestionAndAnswerId, setNoteQuestionAndAnswerId] = React.useState(null as string | null)
  const [isCloning, setIsCloning] = React.useState(false)
  const [readyForReactNative, setReadyForReactNative] = React.useState<boolean | null>(null)
  const [reactNativeProps, setReactNativeProps] = React.useState<string | null>(null)
  const [cadencePolicyJson, setCadencePolicyJson] = React.useState<string>(
    '{"version":1,"defaults":{},"enforced":{},"derived":{},"requirements":{"allowedValues":{}}}'
  )
  const [cadencePolicyError, setCadencePolicyError] = React.useState<string | null>(null)
  const handleV2GraphqlError = useV2GraphqlMutationErrorHandler()
  const configurationSchema = data?.publicInstallables.find(i => i.id === installable)?.configurationSchema
  const toggleFeatureType = (featureTypeId: string) => {
    setFeatureTypes(current =>
      current.includes(featureTypeId) ? current.filter(type => type !== featureTypeId) : [...current, featureTypeId]
    )
  }

  React.useEffect(() => {
    if (data?.marketplaceListing.installable) {
      const date = moment(parseInt(data.marketplaceListing.releaseDate)).format('YYYY-MM-DD')
      const dateParts = date.split('-')

      if (data.marketplaceListing.previewMessage) {
        setPreviewMessage(JSON.parse(data.marketplaceListing.previewMessage))
      }

      if (data.marketplaceListing.notePreviewMessage) {
        setNotePreviewMessage(JSON.parse(data.marketplaceListing.notePreviewMessage))
      }

      setSupportsFlagship(data?.marketplaceListing?.supportsFlagship ?? false)
      setSupportsNote(data?.marketplaceListing?.supportsNote ?? false)
      setInstallable(data?.marketplaceListing.installable.id)
      setTitle(data?.marketplaceListing.title ?? '')
      setDescription(data?.marketplaceListing.description ?? '')
      setIconUrl(data?.marketplaceListing.icon ?? '')
      setDeveloper(data?.marketplaceListing.creator?.id ?? '')
      setYear(parseInt(dateParts[0]))
      setMonth(parseInt(dateParts[1]))
      setDay(parseInt(dateParts[2]))
      setCategories(data?.marketplaceListing.categories?.map(c => c.id))
      setTags(data?.marketplaceListing.tags ?? '')
      setMessageSet(data?.marketplaceListing.messageSetId ?? null)
      setNoteMessageSet(data?.marketplaceListing.noteMessageSetId ?? null)
      setCarouselMedia1(data?.marketplaceListing.carouselMedia1)
      setCarouselMedia2(data?.marketplaceListing.carouselMedia2)
      setCarouselMedia3(data?.marketplaceListing.carouselMedia3)
      setNoteCarouselMedia1(data?.marketplaceListing.noteCarouselMedia1)
      setNoteCarouselMedia2(data?.marketplaceListing.noteCarouselMedia2)
      setNoteCarouselMedia3(data?.marketplaceListing.noteCarouselMedia3)
      setLanguage(data?.marketplaceListing.language?.id)
      setVersion(data?.marketplaceListing.version)
      setShortDescription(data?.marketplaceListing.shortDescription)
      setIsFeatured(data?.marketplaceListing.isFeatured)
      setIconRef(data?.marketplaceListing.iconMedia?.id ?? null)
      setStagingOnly(data?.marketplaceListing.stagingOnly ?? false)
      setFree(data?.marketplaceListing.free ?? false)
      setQuestionAndAnswerId(data.marketplaceListing.questionAndAnswerId ?? null)
      setNoteQuestionAndAnswerId(data.marketplaceListing.noteQuestionAndAnswerId ?? null)
      setReadyForReactNative(data?.marketplaceListing.readyForReactNative)
    }
  }, [data])

  React.useEffect(() => {
    if (listingFeatureTypesWrapped.data) {
      setFeatureTypes(toEditorialFeatureTypeIds(listingFeatureTypesWrapped.data.featureTypeIds || []))
    }
  }, [listingFeatureTypesWrapped.data])

  React.useEffect(() => {
    if (marketplaceListingPropsWrapped.data) {
      setReactNativeProps(marketplaceListingPropsWrapped.data.propsJson)
    }
  }, [marketplaceListingPropsWrapped.data])

  React.useEffect(() => {
    if (marketplaceListingCadencePolicyWrapped.data) {
      setCadencePolicyJson(marketplaceListingCadencePolicyWrapped.data.policyJson)
    }
  }, [marketplaceListingCadencePolicyWrapped.data])

  const save = async () => {
    setShowErrors(true)

    if (cadencePolicyError) {
      return
    }

    if (!year) {
      return
    }

    if (!listingFeatureTypesWrapped.success) {
      return
    }

    setShowErrors(false)
    setIsSaving(true)

    await Promise.all([
      mutate({
        variables: {
          input: {
            id,
            title,
            description,
            releaseMonth: month,
            releaseYear: year,
            releaseDay: day,
            developer: developer ?? '',
            iconUrl: (iconRef ?? iconUrl)!!,
            installable,
            messageSet,
            noteMessageSet,
            configuration: JSON.stringify(configuration),
            categories,
            language,
            version,
            carouselImage1: carouselMedia1Ref ?? carouselMedia1,
            carouselImage2: carouselMedia2Ref ?? carouselMedia2,
            carouselImage3: carouselMedia3Ref ?? carouselMedia3,
            noteCarouselImage1: noteCarouselMedia1Ref ?? noteCarouselMedia1,
            noteCarouselImage2: noteCarouselMedia2Ref ?? noteCarouselMedia2,
            noteCarouselImage3: noteCarouselMedia3Ref ?? noteCarouselMedia3,
            shortDescription,
            featured: isFeatured,
            stagingOnly,
            free,
            questionAndAnswerId,
            noteQuestionAndAnswerId,
            supportsFlagship,
            supportsNote,
            tags: (tags || '')
              .split(',')
              .map(tag => tag.trim())
              .join(','),
            previewMessage:
              JSON.stringify(previewMessage) !== JSON.stringify(createBoard()) ? JSON.stringify(previewMessage) : null,
            notePreviewMessage:
              JSON.stringify(notePreviewMessage) !== JSON.stringify(Array(3).fill(Array(15).fill(0)))
                ? JSON.stringify(notePreviewMessage)
                : null,
            readyForReactNative
          }
        }
      }),
      mutateV2({
        variables: {
          input: {
            id,
            title,
            description,
            installableId: installable,
            releaseAt: moment(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`)
              .startOf('day')
              .valueOf(),
            developer,
            categoryIds: categories,
            configuration: JSON.stringify(configuration),
            featureTypeIds: featureTypes,
            installDeepLink: `https://platform.vestaboard.com/deeplink/marketplace-listing/${id}/install/ref=deeplink`,
            isFree: free,
            messageSetId: messageSet ?? undefined,
            noteMessageSetId: noteMessageSet ?? undefined,
            versionNumber: version ?? undefined,
            supportsFlagship,
            supportsNote,
            shortDescription,
            notePreviewMessage: notePreviewMessage ? JSON.stringify(notePreviewMessage) : undefined,
            previewMessage: previewMessage ? JSON.stringify(previewMessage) : undefined,
            questionAndAnswerId: questionAndAnswerId ?? undefined,
            noteQuestionAndAnswerId: noteQuestionAndAnswerId ?? undefined,
            stagingOnly: stagingOnly,
            readyForReactNative
          }
        }
      }),
      new Promise(async (resolve, reject) => {
        if (reactNativeProps) {
          const variables = {
            input: {
              marketplaceListingId: id,
              propsJson: reactNativeProps
            }
          }
          const result = marketplaceListingPropsWrapped.notFound
            ? await createProps({ variables })
            : await updateProps({ variables })

          handleV2GraphqlError(
            marketplaceListingPropsWrapped.notFound ? 'createProps' : 'updateProps',
            result.data && 'createMarketplaceListingPropsV2' in result.data
              ? result.data.createMarketplaceListingPropsV2
              : result.data?.updateMarketplaceListingPropsV2,
            result.errors
          )
        }
        resolve(true)
      }),
      new Promise(async resolve => {
        if (cadencePolicyJson) {
          const variables = {
            input: {
              marketplaceListingId: id,
              policyJson: cadencePolicyJson
            }
          }
          const result = marketplaceListingCadencePolicyWrapped.notFound
            ? await createCadencePolicy({ variables })
            : await updateCadencePolicy({ variables })

          handleV2GraphqlError(
            marketplaceListingCadencePolicyWrapped.notFound ? 'createCadencePolicy' : 'updateCadencePolicy',
            result.data && 'createMarketplaceListingCadencePolicyV2' in result.data
              ? result.data.createMarketplaceListingCadencePolicyV2
              : result.data?.updateMarketplaceListingCadencePolicyV2,
            result.errors
          )
        }
        resolve(true)
      })
    ])
    await Promise.all([
      refetch(),
      marketplaceListingPropsResponse.refetch(),
      marketplaceListingCadencePolicyResponse.refetch()
    ])

    addToast('Marketplace Listing saved', {
      appearance: 'success',
      autoDismiss: true
    })

    setIsSaving(false)
  }

  if (
    loading ||
    listingFeatureTypesWrapped.loading ||
    marketplaceListingPropsWrapped.loading ||
    marketplaceListingCadencePolicyWrapped.loading
  )
    return <CircularProgress />

  if (!data) return <Alert severity='error'>Marketplace Listing not found</Alert>

  const extraFeatureTypes = featureTypes
    .filter(id => !CURATED_FEATURE_TYPE_IDS.has(id))
    .map(id => ({
      id,
      title: data.marketplaceListingFeatureTypes.find(featureType => featureType.id === id)?.title ?? id
    }))

  const dateValue = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

  const getConfigSchema = (str: string) => {
    try {
      return JSON.parse(str)
    } catch (err) {
      return {}
    }
  }
  return (
    <>
      <Confirm
        title='Clone Marketplace Listing?'
        open={isCloning}
        handleClose={() => {
          setIsCloning(false)
        }}
        handleAccept={async () => {
          setIsCloning(false)
          await clone(data?.marketplaceListing, marketplaceListingPropsWrapped.data)
        }}
      />
      {isEditingPreviewMessage ? (
        <CreateMessageModal
          onSaved={(message: any) => {
            setPreviewMessage(message?.characters)
            setIsEditingPreviewMessage(false)
          }}
          onClose={() => {
            setIsEditingPreviewMessage(false)
          }}
        />
      ) : null}
      {isEditingNotePreviewMessage ? (
        <CreateMessageModal
          boardStyle='note-black'
          onSaved={(message: any) => {
            setNotePreviewMessage(message?.characters)
            setIsEditingNotePreviewMessage(false)
          }}
          onClose={() => {
            setIsEditingNotePreviewMessage(false)
          }}
        />
      ) : null}
      <Box>
        <Card style={{ maxWidth: 850 }}>
          <Table>
            <TableRow>
              <TableCell>Marketplace Listing ID</TableCell>
              <TableCell>
                <TextField value={data.marketplaceListing.id} disabled variant='outlined' fullWidth />
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Supported In</TableCell>
              <TableCell>
                <Box display='flex' alignItems='center'>
                  <Checkbox
                    color='primary'
                    checked={supportsFlagship}
                    onChange={() => {
                      setSupportsFlagship(supportsFlagship => !supportsFlagship)
                    }}
                  />{' '}
                  <Typography variant='body2'>Flagship</Typography>
                </Box>
                <Box display='flex' alignItems='center'>
                  <Checkbox
                    color='primary'
                    checked={supportsNote}
                    onChange={() => {
                      setSupportsNote(supportsNote => !supportsNote)
                    }}
                  />{' '}
                  <Typography variant='body2'>Note</Typography>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Developer</TableCell>
              <TableCell>
                <SelectDeveloper value={developer} setValue={setDeveloper} />
              </TableCell>
              <TableCell>
                <Button disabled={!developer} onClick={() => history.push(`/developers/${developer}`)}>
                  Details
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Installable</TableCell>
              <TableCell>
                <Select
                  required
                  error={showErrors && !installable}
                  value={installable}
                  onChange={e => setInstallable(e.target.value as string)}
                  variant='outlined'
                  fullWidth>
                  {data.publicInstallables
                    .sort((d1, d2) => d1?.title?.localeCompare(d2?.title ?? '') ?? 0)
                    .map(d => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.title}
                      </MenuItem>
                    ))}
                </Select>
              </TableCell>
              <TableCell>
                <Button disabled={!installable} onClick={() => history.push(`/installables/${installable}`)}>
                  Details
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Message set (optional)</TableCell>
              <TableCell>
                <SelectMessageSet messageSet={messageSet} setMessageSet={setMessageSet} />
              </TableCell>
              <TableCell>
                <Button disabled={!messageSet} onClick={() => history.push(`/message-sets/${messageSet}`)}>
                  Details
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Note message set (optional)</TableCell>
              <TableCell>
                <SelectMessageSet messageSet={noteMessageSet} setMessageSet={setNoteMessageSet} isNote={true} />
              </TableCell>
              <TableCell>
                <Button disabled={!noteMessageSet} onClick={() => history.push(`/message-sets/${noteMessageSet}`)}>
                  Details
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Q&A set (optional)</TableCell>
              <TableCell>
                <SelectQa value={questionAndAnswerId} setValue={setQuestionAndAnswerId} />
              </TableCell>
              <TableCell>
                <Button
                  disabled={!questionAndAnswerId}
                  onClick={() => history.push(`/questions-and-answers/${questionAndAnswerId}`)}>
                  Details
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Note Q&A set (optional)</TableCell>
              <TableCell>
                <SelectQa value={noteQuestionAndAnswerId} setValue={setNoteQuestionAndAnswerId} isNote />
              </TableCell>
              <TableCell>
                <Button
                  disabled={!noteQuestionAndAnswerId}
                  onClick={() => history.push(`/questions-and-answers/${noteQuestionAndAnswerId}`)}>
                  Details
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Categories</TableCell>
              <TableCell>
                <Select
                  multiple
                  multiline
                  fullWidth
                  value={categories}
                  variant='outlined'
                  onChange={e => setCategories(e.target.value as string[])}>
                  {data.installableCategories
                    .sort((d1, d2) => d1?.title?.localeCompare(d2?.title ?? '') ?? 0)
                    .map(d => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.title}
                      </MenuItem>
                    ))}
                </Select>
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>Feature Types</TableCell>
              <TableCell>
                {PRIMARY_FEATURE_TYPES.map(featureType => (
                  <FeatureTypeCheckboxRow
                    key={featureType.id}
                    featureType={featureType}
                    selectedIds={featureTypes}
                    disabled={!listingFeatureTypesWrapped.success}
                    onToggle={toggleFeatureType}
                  />
                ))}
                {extraFeatureTypes.map(featureType => (
                  <FeatureTypeCheckboxRow
                    key={featureType.id}
                    featureType={featureType}
                    selectedIds={featureTypes}
                    disabled={!listingFeatureTypesWrapped.success}
                    onToggle={toggleFeatureType}
                  />
                ))}
                <Box marginTop={1}>
                  <Button
                    size='small'
                    variant='text'
                    onClick={() => setLegacyFeatureTypesOpen(open => !open)}
                    style={{ textTransform: 'none', paddingLeft: 0 }}>
                    {legacyFeatureTypesOpen ? 'Hide legacy collections' : 'Show legacy collections'}
                  </Button>
                  <Collapse in={legacyFeatureTypesOpen}>
                    <Typography variant='body2' color='textSecondary' style={{ marginBottom: 8 }}>
                      Home Essentials and Work Essentials still power older app versions and will be removed. Prefer
                      Essentials for new curation.
                    </Typography>
                    {LEGACY_FEATURE_TYPES.map(featureType => (
                      <FeatureTypeCheckboxRow
                        key={featureType.id}
                        featureType={featureType}
                        selectedIds={featureTypes}
                        disabled={!listingFeatureTypesWrapped.success}
                        onToggle={toggleFeatureType}
                      />
                    ))}
                  </Collapse>
                </Box>
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>Language</TableCell>
              <TableCell>
                <Select
                  variant='outlined'
                  fullWidth
                  value={language}
                  onChange={e => setLanguage(e.target.value as string)}>
                  {data.languages.map(d => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.language}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>
                <TextField
                  required
                  error={showErrors && !version}
                  onChange={e => setVersion(e.target.value as string)}
                  value={version}
                  variant='outlined'
                  fullWidth
                />
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell style={{ width: 320, maxWidth: 320, whiteSpace: 'normal' }}>
                <Box>
                  <Typography>Ready for V2 React-Native UI</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    Marks this listing as compatible with the V2 React Native renderer (rather than the legacy iframe
                    UI). Compatible means it has been verified and configured with valid V2 properties below.
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Switch checked={readyForReactNative ?? false} onChange={(e, v) => setReadyForReactNative(v)} />
              </TableCell>
            </TableRow>
          </Table>
        </Card>
        <Spacer size='extraLarge' />
        <FlexHorizontal spaceBetween>
          <Typography variant='h6'>Standard Listing Details</Typography>
        </FlexHorizontal>
        <Card style={{ marginTop: 30, maxWidth: 850 }}>
          <Table>
            <TableRow>
              <TableCell style={{ width: 300 }}>Title</TableCell>
              <TableCell>
                <TextField
                  required
                  error={showErrors && !title}
                  onChange={e => setTitle(e.target.value as string)}
                  value={title}
                  variant='outlined'
                  fullWidth
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell>
                <TextField
                  required
                  error={showErrors && !description}
                  onChange={e => setDescription(e.target.value as string)}
                  value={description}
                  variant='outlined'
                  fullWidth
                  multiline
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Short Description</TableCell>
              <TableCell>
                <TextField
                  required
                  onChange={e => setShortDescription(e.target.value as string)}
                  value={shortDescription}
                  fullWidth
                  variant='outlined'
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Release date</TableCell>
              <TableCell>
                <TextField
                  variant='outlined'
                  fullWidth
                  error={showErrors && !year}
                  required
                  type='date'
                  onChange={e => {
                    const date = e.target.value.split('-')

                    setYear(parseInt(date[0]))
                    setMonth(parseInt(date[1]))
                    setDay(parseInt(date[2]))
                  }}
                  value={dateValue}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Is Featured</TableCell>
              <TableCell>
                <Switch checked={isFeatured ?? false} onChange={e => setIsFeatured(e.target.checked)} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Only available to internal users (staging mode)</TableCell>
              <TableCell>
                <Switch checked={stagingOnly} onChange={e => setStagingOnly(e.target.checked)} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Available for free to users without Plus</TableCell>
              <TableCell>
                <Switch checked={free} onChange={e => setFree(e.target.checked)} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Deep link to install</TableCell>
              <TableCell>
                <TextField
                  variant='outlined'
                  fullWidth
                  value={`https://platform.vestaboard.com/deeplink/marketplace-listing/${id}/install/ref=deeplink`}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Tags</TableCell>
              <TableCell align='right'>
                <Tags onChange={setTags} value={tags} />
                <Spacer />
                <CsvTagsImport onImport={setTags} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Icon</TableCell>
              <TableCell>
                <MediaUploadButton
                  previewUrl={iconUrl}
                  mediaId={iconRef}
                  onUpload={id => setIconRef(id)}
                  onBlobUpload={async (blob: Blob) => {
                    const result = await getIconUploadUrl({
                      variables: {
                        input: {
                          marketplaceListingId: id
                        }
                      }
                    })

                    const uploadUrl =
                      result.data?.uploadMarketplaceListingLogoV2?.__typename === 'MarketplaceListingUploadV2'
                        ? result.data?.uploadMarketplaceListingLogoV2.uploadUrl
                        : undefined

                    await uploadV2Media(blob, uploadUrl)
                  }}
                />
              </TableCell>
            </TableRow>
            {supportsFlagship ? (
              <>
                <TableRow>
                  <TableCell>Flagship Carousel Media 1</TableCell>
                  <TableCell>
                    <MediaUploadButton
                      previewUrl={carouselMedia1}
                      mediaId={carouselMedia1Ref}
                      onUpload={id => setCarouselMedia1Ref(id)}
                      aspectRatio={16 / 9}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Flagship Carousel Media 2</TableCell>
                  <TableCell>
                    <MediaUploadButton
                      previewUrl={carouselMedia2}
                      mediaId={carouselMedia2Ref}
                      onUpload={id => setCarouselMedia2Ref(id)}
                      aspectRatio={16 / 9}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Flagship Carousel Media 3</TableCell>
                  <TableCell>
                    <MediaUploadButton
                      previewUrl={carouselMedia3}
                      mediaId={carouselMedia3Ref}
                      onUpload={id => setCarouselMedia3Ref(id)}
                      aspectRatio={16 / 9}
                    />
                  </TableCell>
                </TableRow>
              </>
            ) : null}
            {supportsNote ? (
              <>
                <TableRow>
                  <TableCell>Note Carousel Media 1</TableCell>
                  <TableCell>
                    <MediaUploadButton
                      previewUrl={noteCarouselMedia1}
                      mediaId={noteCarouselMedia1Ref}
                      onUpload={id => setNoteCarouselMedia1Ref(id)}
                      aspectRatio={16 / 9}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Note Carousel Media 2</TableCell>
                  <TableCell>
                    <MediaUploadButton
                      previewUrl={noteCarouselMedia2}
                      mediaId={noteCarouselMedia2Ref}
                      onUpload={id => setNoteCarouselMedia2Ref(id)}
                      aspectRatio={16 / 9}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Note Carousel Media 3</TableCell>
                  <TableCell>
                    <MediaUploadButton
                      previewUrl={noteCarouselMedia3}
                      mediaId={noteCarouselMedia3Ref}
                      onUpload={id => setNoteCarouselMedia3Ref(id)}
                      aspectRatio={16 / 9}
                    />
                  </TableCell>
                </TableRow>
              </>
            ) : null}
            {supportsFlagship ? (
              <TableRow>
                <TableCell>Preview Message</TableCell>
                <TableCell>
                  <MessageCard
                    characters={previewMessage}
                    onEditPress={() => {
                      setIsEditingPreviewMessage(true)
                    }}
                  />
                </TableCell>
              </TableRow>
            ) : null}
            {supportsNote ? (
              <TableRow>
                <TableCell>Note Preview Message</TableCell>
                <TableCell>
                  <MessageCard
                    boardStyle='note-black'
                    characters={notePreviewMessage}
                    onEditPress={() => {
                      setIsEditingNotePreviewMessage(true)
                    }}
                  />
                </TableCell>
              </TableRow>
            ) : null}
          </Table>
        </Card>
        <Spacer size='extraLarge' />
        <FlexVertical spaceBetween>
          <Typography variant='h6'>Legacy (V1) Installable UI Properties (iframe)</Typography>
          <Typography variant='body2' color='textSecondary'>
            Configure the custom properties the V1 (iframe) installable UI reads for this marketplace listing. These
            values only apply to the legacy iframe-based renderer.
          </Typography>
        </FlexVertical>
        <Card style={{ marginTop: 30, maxWidth: 850 }}>
          {data.marketplaceListing.configuration && configurationSchema && configurationSchema !== '{}' ? (
            <Table>
              <ConfigurationSchemaForm
                values={JSON.parse(data.marketplaceListing.configuration)}
                onChange={setConfiguration}
                schema={getConfigSchema(configurationSchema)}
              />
            </Table>
          ) : (
            <Alert severity='info'>No configuration found.</Alert>
          )}
        </Card>
        <Spacer size='extraLarge' />
        <V2InstallablePropsEditor
          installableDefinition={installableDefinition}
          value={reactNativeProps ?? '{}'}
          onChange={setReactNativeProps}
          hasError={!marketplaceListingPropsWrapped.notFound && !marketplaceListingPropsWrapped.success}
        />
        <Spacer size='extraLarge' />
        <FlexVertical spaceBetween>
          <Typography variant='h6'>Cadence Policy</Typography>
          <Typography variant='body2' color='textSecondary'>
            Configure defaults, constraints, and allowed values for cadence fields using schema-aligned options.
          </Typography>
        </FlexVertical>
        <Box style={{ marginTop: 30, maxWidth: 980 }}>
          <CadencePolicyEditor
            value={cadencePolicyJson}
            onChange={(nextValue, error) => {
              setCadencePolicyJson(nextValue)
              setCadencePolicyError(error)
            }}
          />
          {cadencePolicyError ? <Alert severity='error'>{cadencePolicyError}</Alert> : null}
        </Box>
      </Box>
      <Spacer size='extraLarge' />
      <FlexHorizontal>
        <Button
          size='large'
          variant='contained'
          onClick={() => {
            history.push('/marketplace-listings')
          }}>
          Cancel
        </Button>
        <Box width={24} />
        <Button
          disabled={isSaving || !listingFeatureTypesWrapped.success}
          onClick={save}
          variant='contained'
          size='large'
          color='primary'>
          Save
        </Button>
        <Box width={24} />
        <Button
          disabled={cloning}
          size='large'
          variant='contained'
          onClick={() => {
            setIsCloning(true)
          }}>
          {cloning ? 'Cloning...' : 'Clone Listing'}
        </Button>
      </FlexHorizontal>
    </>
  )
}
